/*
 * Copyright (c) 2022-2025.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import TextInput from "./TextInput.jsx";
import {Util} from "../../Utilities.js";
import DisplayIcon from "../Icons/DisplayIcon.jsx";
import Dropdown from "../Dropdown.jsx";
import DetectOutsideClick from "../DetectOutsideClick.jsx";
import DropdownExpand from "../DropdownExpand.jsx";
import Unique from "toolbox/unique";
import Algo from "toolbox/algo";
import Color from "toolbox/color";
import Strings from "toolbox/strings";

const defaultWidth = "20vw"
const defaultHeight = "20vh"

function Select({
                    value,
                    options = [],
                    filter,
                    className = "",
                    width,
                    onChange,
                    onFocus,
                    isReadOnly,
                    placeholder,
                    onBlur,
                    isOpen: propsIsOpen,
                    onShiftClick
                }) {
    const [searchValue, setSearchValue] = useState("");
    const [currentValue, setCurrentValue] = useState("");
    const [selectedItem, setSelectedItem] = useState(-1);
    const [isOpen, setIsOpen] = useState(false);
    const [dialogWidth, setDialogWidth] = useState(defaultWidth);
    const [dialogHeight, setDialogHeight] = useState(defaultHeight);
    const [dropdownInstanceId, setDropdownInstanceId] = useState(Unique.newGlobalId())
    const [currentColor, setCurrentColor] = useState()

    const isPressed = false
    let hasFastNavigation = !Strings.isUndefined(onShiftClick) && isPressed

    const inputRef = useRef(null);

    function setData() {
        let optionIndex = options.findIndex(item => ((item.value === (value?.[0]?.value ?? "")) || (item.value === (value?.value ?? ""))))
        let option = options[optionIndex]
        if (option !== undefined) {
            setSearchValue(option.label)
            setCurrentValue(option.value)
            setCurrentColor(option.color)
            setSelectedItem(optionIndex)

        } else {
            setSearchValue("")
            setCurrentValue("")
        }

    }

    useEffect(() => {
        setData()
    }, [options, value]);

    function setOption(newOption) {
        if (newOption !== undefined) {

            onChange?.(newOption)
            setSearchValue(newOption.label)
            setCurrentValue(newOption.value)
            setCurrentColor(newOption.color)
            setIsOpen(false)
        }
    }

    useEffect(() => {
        if (!Strings.isUndefined(propsIsOpen)) {
            setIsOpen(propsIsOpen);
        }
    }, [propsIsOpen]);


    if (filter !== undefined) {
        options = options.filter(item => filter(item))
    }

    let selectedOption = options.find(option => (option.value === currentValue))
    let focusedOption = options.find((option, index) => selectedItem === index)

    let suggestion = focusedOption?.label ?? ""


    return (
        <div className={className}
             style={{position: "static", display: "inline-block", width: width}}>

            <TextInput
                value={searchValue}
                suggestion={suggestion}
                innerRef={inputRef}
                className={`${(hasFastNavigation) && Util.twin.fastNavigateStyle()}`}
                hasArrow={((!isOpen && !isReadOnly))}
                hasClose={isOpen}
                hasDot={(!Strings.isEmpty(currentColor))}
                dotColor={currentColor || 'rgba(107, 114, 128, 100)'}
                isReadOnly={isReadOnly}
                arrowColor={'rgba(107, 114, 128, 100)'}
                closeColor={'rgba(107, 114, 128, 100)'}
                style={{
                    width: '100%',
                    display: "inline-block",
                    BoxShadow: "none",
                    border: "none"
                }}
                leadingIconName={selectedOption?.iconName}
                onPaste={async (event) => {
                    let input = event.clipboardData.getData('text')

                    // update value on correct type
                    options.forEach((option) => {
                        if (option.label === input) {
                            event.preventDefault()
                            setOption(option)
                            if (onChange !== undefined) {
                                onChange(option)
                            }
                        }
                    })


                }}

                onChange={value => {
                    // update the input
                    setSearchValue(value)

                    // search in list
                    let bestScore = 100
                    let bestIndex = 0
                    options.map((option, index) => {
                        let score = Algo.levenshteinDistance(option.label, value)

                        if (score < bestScore) {
                            bestScore = score
                            bestIndex = index
                        }

                        (option.synonyms ?? []).map(synonym => {
                            let score2 = Algo.levenshteinDistance(synonym, value)
                            if (score2 < bestScore) {
                                bestScore = score2
                                bestIndex = index
                            }
                        })
                    })

                    // scroll to the element in the dropdown
                    let element = document.getElementById(dropdownInstanceId + ":I=" + (bestIndex))
                    if (element !== null) {
                        let container = document.getElementById(dropdownInstanceId + ":C")
                        container.scroll({
                            top: element.offsetTop - 1, //scroll to the top of the element + table header
                            behavior: 'smooth' //auto, smooth, initial, inherit
                        });
                    }

                    setSelectedItem(bestIndex)


                    if (value.length === 0) {
                        onChange?.(null)

                    }

                }}
                onKeyDown={(event) => {

                    if (isOpen === false) {
                        setIsOpen(true);
                    }
                    // Check if Ctrl or MetaKey is pressed. If so, prevent the actions of this element to allow the slider shortcuts to work.
                    if (!event.metaKey && !event.ctrlKey) {
                        // Block the up and Down Keys from accessing the Input
                        if (event.key === "ArrowDown") {
                            event.preventDefault();
                            event.stopPropagation();
                            let dataLength = options.length
                            let newSelectedItemIndex = (dataLength <= selectedItem + 1) ? (selectedItem) : selectedItem + 1

                            // scroll to the element in the dropdown
                            let element = document.getElementById(dropdownInstanceId + ":I=" + newSelectedItemIndex)
                            if (element !== null) {
                                let container = document.getElementById(dropdownInstanceId + ":C")
                                container.scroll({
                                    top: element.offsetTop - 2, //scroll to the top of the element
                                    behavior: 'smooth' //auto, smooth, initial, inherit
                                });
                            }

                            setSelectedItem(newSelectedItemIndex)


                        } else if (event.key === "ArrowUp") {
                            event.preventDefault();
                            event.stopPropagation();
                            let newSelectedItemIndex = (selectedItem !== 0) ? selectedItem - 1 : 0

                            // scroll to the element in the dropdown
                            let element = document.getElementById(dropdownInstanceId + ":I=" + newSelectedItemIndex)
                            if (element !== null) {
                                let container = document.getElementById(dropdownInstanceId + ":C")
                                container.scroll({
                                    top: element.offsetTop - 2,  //scroll to the top of the element
                                    behavior: 'smooth' //auto, smooth, initial, inherit
                                });
                            }

                            setSelectedItem(newSelectedItemIndex)

                        } else if (event.key === "Enter") {
                            event.preventDefault();
                            event.stopPropagation();

                            let option = options[selectedItem]
                            setOption(option)

                        } else if (event.key === "Escape") {
                            // keep the current selection and close the dialog
                            event.preventDefault();
                            event.stopPropagation();

                            let option = options.find(item => (item.value === currentValue))
                            setOption(option)

                        } else if (event.key === "Tab") {

                            // if tab is used, the focus is gone and close the dialog
                            setIsOpen(false)


                        }
                    }
                }}
                onClick={(event) => {
                    if (event.metaKey || event.ctrlKey) {
                        event.preventDefault()
                        event.stopPropagation()

                        onShiftClick?.(selectedOption)

                    } else {
                        setIsOpen(true);
                    }
                }}
                onFocus={() => {
                    onFocus?.()

                }}
                onBlur={() => {
                    onBlur?.()

                }}
                placeholder={placeholder ?? "---"}
                onCloseClick={() => {
                    setIsOpen(false);

                }}
                onFocusCapture={() => setIsOpen(true)}
            />

            <Dropdown targetRef={inputRef}
                      isOpen={isOpen} width={dialogWidth} height={dialogHeight}>
                <DetectOutsideClick additionalRef={inputRef} isOpen={isOpen} onOutsideClick={() => {
                    setIsOpen(false);

                }} className={"h-full h-full"}>

                    <div
                        className={"customScrollbarSmall space-y-1 p-2 scroll-pt-2 overflow-y-scroll w-full h-full"}
                        style={{
                            scrollSnapType: "y mandatory",
                            scrollSnapPointsY: "repeat(38px)",
                        }}
                        id={dropdownInstanceId + ":C"}>

                        {options.map((option, index) => {

                            let isSelected = (option.value === currentValue)
                            let hasCursor = selectedItem === index

                            let iconColor = Color.getTextColorForBackground(option?.color ?? "#fffff")

                            return (
                                <div
                                    id={dropdownInstanceId + ":I=" + index}
                                    key={dropdownInstanceId + ":I=" + index}
                                    onClick={(event) => {
                                        if (event.metaKey || event.ctrlKey) {
                                            event.preventDefault()
                                            event.stopPropagation()

                                            onShiftClick?.(option)

                                        } else {
                                            event.preventDefault()
                                            event.stopPropagation()
                                            setOption(option)
                                        }
                                    }}
                                    style={{
                                        borderLeft: hasCursor ? "2px solid #2563EB" : "2px solid #ffff",
                                        scrollSnapAlign: "start"
                                    }}
                                    className={`relative  p-2 flex flex-col cursor-pointer border border-white focus:outline-hidden  b z-10 hover:bg-blue-50 rounded-sm ${(isSelected) && ("bg-blue-50!  border-blue-400!")} ${hasCursor && ("bg-blue-50")}`}>
                                    <div className="flex items-center text-sm">
                                        {(option?.color !== undefined && option?.iconName !== undefined && option?.iconName !== null) && (
                                            <div
                                                style={{
                                                    background: option.color,
                                                    color: iconColor
                                                }}
                                                className={"h-5 w-5 p-1 bg-gray-200 shrink-0 rounded-sm mr-1.5 flex justify-center items-center"}>
                                                {(option?.iconName !== undefined) && (
                                                    <DisplayIcon iconName={option.iconName}
                                                                 color={iconColor}/>
                                                )}
                                            </div>
                                        )}
                                        {(option?.color === undefined && option?.iconName !== undefined && option?.iconName !== null) && (
                                            <div
                                                className={"h-6 w-6   shrink-0  mr-1.5 flex justify-center text-stone-400 items-center"}>
                                                {(option?.iconName !== undefined && option?.iconName !== null) && (
                                                    <DisplayIcon iconName={option.iconName} size={"1rem"}/>
                                                )}
                                            </div>
                                        )}

                                        <span
                                            className={` font-medium text-gray-600 grow truncate ${(hasFastNavigation) && Util.twin.fastNavigateStyle()} ${isSelected && (" text-blue-600!")}`}>{option.label || "[Lable not found]"}</span>


                                        {isSelected && (
                                            <DisplayIcon iconName={"Check"}
                                                         className={"text-blue-600"}/>)}

                                    </div>
                                </div>)


                        })}
                        <br/>
                        <br/>
                        <br/>
                        <br/>
                    </div>

                    <DropdownExpand width={dialogWidth} height={dialogHeight}
                                    defaultWidth={defaultWidth}
                                    defaultHeight={defaultHeight} onChange={(newWidth, newHeight) => {
                        setDialogWidth(newWidth)
                        setDialogHeight(newHeight)
                    }}/>

                </DetectOutsideClick>
            </Dropdown>


        </div>
    )

}

Select.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
    ]),
    options: PropTypes.array,
    onChange: PropTypes.func,
    isReadOnly: PropTypes.bool

};

Select.defaultProps = {
    options: []
}


export default Select;